// src/utils/lossRunPrompt.js
//
// The plain-text briefing the user pastes into Claude chat to get loss-run
// request emails drafted (the MCP connector drafts in the insurance mailbox;
// this app never sends anything). Written as information for a capable
// assistant, not a list of orders: two hard rules, then context, then the
// facts exactly as stored from an allowlist of fields — never premium,
// commission, fees, payroll, FEIN or minutes.
//
// scope: { sourceId } for one source, or 'all open' for every source the to-do
// list says to ask. A DoNotContact source is never included and asking for one
// returns { blocked }.

import {
  dateOnlyOf, isLossRunEligible, termOf, boolOrNull, authorizationState, authorizationNeed, requestsForPolicy,
  latestRequestOf, requestState, sortRequestsNewestFirst, OWN_AGENCY_NAME, LOSS_RUN_FRESH_DAYS, UNASSIGNED_ID,
} from './lossRunStatus';
import { buildCoverageGrid, needForLob, NEED_LABELS } from './lossRunGrid';
import { buildTodo } from './lossRunTodo';
import { currentSeason, resolutionIndex, splitResolutions, resolutionSentence, resolutionCoversPolicy } from './lossRunResolutions';

export const PROMPT_MAILBOX = 'marc@myfiginsurance.com';
export const PROMPT_VALUED_WITHIN_DAYS = 30;
export const PROMPT_NEVER_CONTACT = 'Texas First Insurance';
export const PROMPT_HARD_RULES = [
  'Draft only. Never send anything without my explicit OK.',
  `Never contact, draft to, or copy ${PROMPT_NEVER_CONTACT}.`,
];

const blank = v => v === null || v === undefined || String(v).trim() === '';
const stored = v => (blank(v) ? '(blank)' : String(v).trim());
const d = v => dateOnlyOf(v) || '(blank)';
const idOf = v => (blank(v) ? UNASSIGNED_ID : String(v).trim());
const plural = (n, one, many) => (n === 1 ? one : many);

function policiesBlock(pols) {
  const byTerm = new Map();
  for (const p of pols) {
    const t = termOf(p) || 'Term not on record';
    if (!byTerm.has(t)) byTerm.set(t, []);
    byTerm.get(t).push(p);
  }
  const out = [];
  for (const [term, list] of [...byTerm.entries()].sort((a, b) => b[0].localeCompare(a[0]))) {
    out.push(`  ${term}`);
    for (const p of [...list].sort((a, b) => (dateOnlyOf(b.EffectiveDate) || '').localeCompare(dateOnlyOf(a.EffectiveDate) || ''))) {
      out.push(`    - ${stored(p.LOB)} · ${stored(p.Carrier)} · policy ${stored(p.PolicyNumber)} · ${d(p.EffectiveDate)} to ${d(p.ExpirationDate)} · agency of record: ${stored(p.AgencyOfRecord)} · FIG_Policies id ${p.id}`);
    }
  }
  return out;
}

// Why the insured's authorization is (or is not) needed, as a plain statement.
function authorizationWhy(need) {
  const parts = [];
  if (need.override === true) parts.push('the source requires it');
  if (need.override === false) parts.push('the source does not require it');
  if (need.needing > 0) parts.push(`${need.needing} of ${need.total} ${plural(need.total, 'policy was', 'policies were')} written by another agency`);
  else if (need.total > 0) parts.push(`all ${need.total} ${plural(need.total, 'policy was', 'policies were')} written by ${OWN_AGENCY_NAME}`);
  if (need.unknown > 0) parts.push(`${need.unknown} ${plural(need.unknown, 'has', 'have')} no agency of record on file`);
  return parts.join('; ');
}

function sourceFacts({ source, pols, requests, today, index, resolutions = [], season, policiesById }) {
  const need = authorizationNeed(source, pols);
  const lines = [...new Set(pols.map(p => String(p.LOB ?? '').trim()).filter(Boolean))].sort();
  const tags = lines.map(l => `${l}: ${NEED_LABELS[needForLob(l)]}`);
  const out = [];
  out.push(`SOURCE ${index}: ${stored(source.Title)}`);
  out.push(`  Kind: ${stored(source.Kind)}`);
  out.push(`  Contact name: ${stored(source.ContactName)}`);
  out.push(`  Email: ${stored(source.ContactEmail)}`);
  out.push(`  Phone: ${stored(source.ContactPhone)}`);
  out.push(`  Default method on file: ${stored(source.DefaultMethod)}`);
  out.push(`  Notes: ${stored(source.Notes)}`);
  out.push(`  Lines and how they are usually treated: ${tags.join('; ') || '(none)'}`);
  out.push(`  Insured authorization for these policies: ${need.need}${authorizationWhy(need) ? ` — ${authorizationWhy(need)}` : ''}`);
  out.push(`  Policies (eligible; quotes and submissions excluded):`);
  out.push(...policiesBlock(pols));
  const srcRequests = sortRequestsNewestFirst((requests || []).filter(r => idOf(r.LossRunSourceId) === String(source.id)));
  if (srcRequests.length === 0) out.push(`  Already on file from this source: nothing`);
  else {
    out.push(`  Already on file from this source:`);
    for (const r of srcRequests) {
      const st = requestState(r, today);
      const coveredIds = blank(r.PolicyIds) ? 'all policies of this source' : `FIG_Policies ids ${String(r.PolicyIds).trim()}`;
      const status = st.color === 'green' ? 'current'
        : st.color === 'yellow' ? (dateOnlyOf(r.DateReceived) ? `stale (older than ${LOSS_RUN_FRESH_DAYS} days)` : 'requested, waiting')
          : st.color;
      out.push(`    - ${stored(r.Method)} · requested ${d(r.DateRequested)} · received ${d(r.DateReceived)} · valued as of ${d(r.ValuedAsOf)} · terms covered: ${stored(r.TermsCovered)} · covers ${coveredIds} · ${status}`);
    }
  }
  const notCovered = pols.filter(p => {
    const latest = latestRequestOf(requestsForPolicy(requests, p));
    return !latest || requestState(latest, today).color !== 'green';
  });
  out.push(`  Policies with no current loss run on file: ${notCovered.length === 0 ? 'none' : notCovered.map(p => `${stored(p.PolicyNumber)} (${stored(p.LOB)}, ${termOf(p) || 'term not on record'})`).join('; ')}`);
  // Resolutions for this source's policies: this season, then history.
  const mine = (resolutions || []).filter(r => pols.some(p => resolutionCoversPolicy(r, p)));
  const { active, history } = splitResolutions(mine, season);
  const handledIdx = resolutionIndex(pols, resolutions, season, policiesById);
  out.push(`  Handled this renewal${season ? ` (${season})` : ''}: ${active.length === 0 ? 'nothing recorded' : ''}`);
  for (const r of active) out.push(`    - ${resolutionSentence(r, policiesById)}`);
  out.push(`  How it was handled before: ${history.length === 0 ? 'nothing recorded' : ''}`);
  for (const r of history) out.push(`    - ${stored(r.RenewalYear)} renewal — ${resolutionSentence(r, policiesById)}`);
  const toAsk = notCovered.filter(p => !handledIdx.get(String(p.id))?.handled);
  const handledCount = notCovered.length - toAsk.length;
  if (handledCount > 0) out.push(`  Of those, already handled this renewal (do not ask): ${notCovered.filter(p => handledIdx.get(String(p.id))?.handled).map(p => stored(p.PolicyNumber)).join('; ')}`);
  if (toAsk.length === 0) {
    out.push(`  What I need from them: ${notCovered.length === 0 ? 'Nothing is open for this source; everything on file is current.' : 'Nothing to ask for; everything not on file is already handled this renewal.'}`);
  } else {
    out.push(`  What I need from them: currently valued loss runs, valued within ${PROMPT_VALUED_WITHIN_DAYS} days, for the ${toAsk.length} ${plural(toAsk.length, 'policy', 'policies')} listed under "Policies with no current loss run on file"${handledCount ? ' that are not already handled' : ''}, and written confirmation where a policy has no reported losses.`);
  }
  const openLetterLine = toAsk.some(p => needForLob(p.LOB) === 'letter');
  return { out, need, open: toAsk.length, openLetterLine };
}

// Earliest / latest ExpirationDate among the insured's current (Active/Bound) policies.
export function renewalWindow(policies) {
  const dates = (policies || [])
    .filter(p => ['active', 'bound'].includes(String(p?.Status ?? '').trim().toLowerCase()))
    .map(p => dateOnlyOf(p.ExpirationDate)).filter(Boolean).sort();
  if (dates.length === 0) return null;
  return { earliest: dates[0], latest: dates[dates.length - 1] };
}

/**
 * buildClaudePrompt({ insured, sources, policies, requests, authorization, today, scope })
 * → string | { blocked: reason }
 */
export function buildClaudePrompt({ insured = {}, sources = [], policies = [], requests = [], resolutions = [], authorization, today, scope } = {}) {
  const todayKey = dateOnlyOf(today) || dateOnlyOf(new Date());
  const auth = authorization || authorizationState(insured, todayKey);
  const byId = new Map((sources || []).map(s => [String(s.id), s]));
  const eligible = (policies || []).filter(isLossRunEligible);
  const policiesById = new Map(eligible.map(p => [String(p.id), p]));
  const season = currentSeason(policies, todayKey);
  const handledIdx = resolutionIndex(eligible, resolutions, season, policiesById);
  const insuredName = String(insured?.Title || insured?.CompanyName || 'the insured').trim();

  let targetIds;
  if (scope && typeof scope === 'object' && scope.sourceId !== undefined) {
    const s = byId.get(String(scope.sourceId));
    if (!s) return { blocked: 'Source not found' };
    if (boolOrNull(s.DoNotContact) === true) return { blocked: 'Do not contact' };
    targetIds = [String(s.id)];
  } else {
    const grid = buildCoverageGrid({ sources, policies: eligible, requests, today: todayKey, handled: handledIdx });
    const todo = buildTodo({ grid, sources, policies: eligible, requests, insured, today: todayKey, handled: handledIdx });
    targetIds = todo.filter(t => t.kind === 'ask' || t.kind === 'find-contact').map(t => t.sourceId);
    if (targetIds.length === 0) return { blocked: 'Nothing open' };
  }

  // Facts first (so the briefing above can refer to what is actually there).
  const sections = [];
  let anyNeedsAuth = false;
  let anyMissingEmail = false;
  let anyLetterMethod = false;
  let anyOpenLetterLine = false;
  targetIds.forEach((sid, i) => {
    const source = byId.get(sid);
    const pols = eligible.filter(p => idOf(p.LossRunSourceId) === sid);
    const { out, need, openLetterLine } = sourceFacts({ source, pols, requests, today: todayKey, index: i + 1, resolutions, season, policiesById });
    sections.push(out);
    if (need.need === 'needed') anyNeedsAuth = true;
    if (blank(source.ContactEmail)) anyMissingEmail = true;
    if (/no known loss letter/i.test(String(source.DefaultMethod ?? ''))) anyLetterMethod = true;
    if (openLetterLine) anyOpenLetterLine = true;
  });
  const sourceNames = targetIds.map(sid => stored(byId.get(sid).Title));
  const one = sourceNames.length === 1;
  const nklModel = sortRequestsNewestFirst((requests || []).filter(r => /no known loss letter/i.test(String(r.Method ?? '')) && !blank(r.VaultPath)))[0] || null;
  const renewal = renewalWindow(policies);

  const out = [];
  out.push(`WHAT I NEED`);
  out.push(`I need loss runs for ${insuredName} from the ${one ? 'source' : 'sources'} below for the next renewal: ${sourceNames.join(', ')}. Help me get them: work out the best way to reach ${one ? 'them' : 'each one'} and draft the ${one ? 'email' : 'emails'} for my review.`);
  out.push(``);
  out.push(`HARD RULES`);
  for (const r of PROMPT_HARD_RULES) out.push(`- ${r}`);
  out.push(``);
  out.push(`GOOD TO KNOW`);
  out.push(`- My insurance mailbox is ${PROMPT_MAILBOX}; that is where carrier and wholesaler threads live.`);
  out.push(`- If there is a recent thread with this contact or company, replying on it is usually better than a cold email. Tell me which thread you found and ask before using it.`);
  out.push(`- The contact below is the one on file and may be stale${anyMissingEmail ? '; for at least one source there is no email on file at all' : ''}. The source Notes are included. If you find a better or more recent contact, tell me.`);
  // Authorization: three distinct cases (on file / recorded as not on file / not recorded).
  let authLine;
  if (auth.onFile === true && auth.usable) {
    authLine = `a signed loss run authorization from ${insuredName} is on file, signed ${auth.signed || '(date blank)'}, at FIGVault path ${stored(auth.vaultPath)}. Ask me whether to attach it.`;
  } else if (auth.onFile === true) {
    authLine = `the only signed loss run authorization on file is from ${auth.signed || '(date blank)'} and is more than a year old (FIGVault path ${stored(auth.vaultPath)}). A fresh one is likely needed; say so before drafting and offer to draft one for the insured to sign.`;
  } else if (auth.onFile === false) {
    authLine = `No signed authorization is on file. Say so before drafting and offer to draft one for the insured to sign.`;
  } else {
    authLine = `Whether a signed authorization exists is not recorded. Ask me before drafting.`;
  }
  out.push(`- Authorization: ${authLine}${anyNeedsAuth ? '' : ' The facts below say per source whether one is needed; for the sources in scope it is not.'}`);
  if (anyOpenLetterLine || anyLetterMethod) {
    out.push(`- No known loss letters: for lines tagged "Letter usually accepted", or when a source cannot produce runs, a signed no known loss letter may satisfy the underwriter.${anyLetterMethod ? ' At least one source below lists a letter as its default method, so a letter path may be the better ask there.' : ''} The wholesaler requires a specific format: on the insured's letterhead, addressed to the quoting carrier(s) c/o the wholesaler, with a table (Policy Period | Coverage | Insurer | Policy No.) listing every policy it covers, any known claim named as an exception, signed by an officer. ${nklModel ? `The most recent one on file is the model: FIGVault path ${stored(nklModel.VaultPath)} (${stored(nklModel.Method)}, received ${d(nklModel.DateReceived)}).` : 'None is on file yet for this insured to use as a model.'} Write dates as "October 1, 2026" style to avoid day/month confusion.`);
  }
  out.push(`- After I confirm an email was sent, offer to log it (FIG_LossRunRequests: InsuredId, LossRunSourceId, Method, DateRequested, TermsCovered, PolicyIds). Show me the row before writing.`);
  out.push(``);
  out.push(`THE FACTS (exact, as stored; do not alter policy numbers, carriers or dates; "(blank)" where blank)`);
  out.push(`Insured: ${insuredName}`);
  out.push(`Our agency (agency of record on our own placements): ${OWN_AGENCY_NAME}`);
  out.push(`Today: ${todayKey}`);
  out.push(`Renewal season being worked: ${season || '(no current Active/Bound policies on file)'}`);
  if (!renewal) out.push(`Next renewal: (no current Active/Bound policies on file)`);
  else if (renewal.earliest === renewal.latest) out.push(`Next renewal: ${renewal.earliest}`);
  else out.push(`Next renewal: ${renewal.earliest} (renewals run from ${renewal.earliest} to ${renewal.latest})`);
  out.push(``);
  for (const sec of sections) { out.push(...sec); out.push(``); }
  return out.join('\n');
}
